import { memo, type CSSProperties } from "react";
import { Maximize2 } from "lucide-react";
import {
  Handle,
  NodeResizer,
  Position,
  useReactFlow,
  type NodeProps,
} from "@xyflow/react";
import { componentRegistry } from "../../core/component-registry";
import type { EditorNode, NodeRendererProps } from "../../core/editor-types";
import { useEditorStore } from "../../store/editor-store";
import { UnknownNode } from "../nodes/BuiltinNodes";
import { useCanvasDocumentWindowStore } from "./document-window-store";
import { useCanvasInteractionSettings } from "./CanvasInteractionContext";
import { COMPONENT_SNAP_THRESHOLD_PX } from "../../core/node-snapping";

const portPositions = [
  ["top", Position.Top],
  ["right", Position.Right],
  ["bottom", Position.Bottom],
  ["left", Position.Left],
] as const;

export const CanvasNode = memo(function CanvasNode({ id, data, selected, width }: NodeProps<EditorNode>) {
  const updateNodeData = useEditorStore((state) => state.updateNodeData);
  const beginNodeResize = useEditorStore((state) => state.beginNodeResize);
  const endNodeResize = useEditorStore((state) => state.endNodeResize);
  const openDocumentWindow = useCanvasDocumentWindowStore((state) => state.open);
  const { getZoom } = useReactFlow();
  const { snapGap, defaultNodeRadius } = useCanvasInteractionSettings();
  const plugin = componentRegistry.get(data.kind);
  const Renderer = plugin?.Renderer ?? UnknownNode;
  const update: NodeRendererProps["update"] = (patch, historyLabel) =>
    updateNodeData(id, patch, historyLabel);

  return (
    <div
      className="canvas-node-shell"
      style={{
        "--node-radius": `${typeof data.borderRadius === "number" ? data.borderRadius : defaultNodeRadius}px`,
      } as CSSProperties}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={plugin?.type === "heading" ? 180 : 140}
        minHeight={plugin?.type === "heading" ? 54 : 72}
        keepAspectRatio={plugin?.type === "image"}
        lineClassName="canvas-resize-line"
        handleClassName="canvas-resize-handle"
        onResizeStart={() => beginNodeResize(
          id,
          COMPONENT_SNAP_THRESHOLD_PX / getZoom(),
          snapGap,
          plugin?.type === "image",
        )}
        onResizeEnd={() => queueMicrotask(endNodeResize)}
      />
      <div
        className="node-hover-drag-handle"
        aria-hidden="true"
      >
        <span className="node-hover-drag-segment drag-handle" title="拖动组件" />
      </div>
      {plugin?.documentWindow ? (
        <button
          type="button"
          className="nodrag nopan node-document-open"
          aria-label={`在大窗口中打开${plugin.title}`}
          title="在大窗口中打开"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            openDocumentWindow(
              id,
              width ?? plugin.defaultSize.width,
              plugin.documentWindow!.defaultSize,
            );
          }}
        >
          <Maximize2 size={13} aria-hidden="true" />
        </button>
      ) : null}
      <div className="canvas-node-renderer">
        <Renderer id={id} data={data} selected={selected} update={update} />
      </div>
      {portPositions.map(([name, position]) => (
        <Handle
          key={name}
          id={name}
          type="source"
          position={position}
          className={`canvas-handle canvas-handle--${name}`}
          aria-label={`${name} 连接点`}
        />
      ))}
    </div>
  );
});
